import { useState } from "react"
import moment from "moment"
import { formatCurrency } from "../../Helpers"
import { applyImpact, formatImpact, FULL_IMPACT, impactParts } from "../../Impact"

// Stats, un seul budget sélectionné : les opérations qui font son total sur la période, à la place de
// « Par budget ». Lecture seule. Par date (découpée par mois si la période en a plusieurs) ou par
// montant. Le montant est l'impact budget ; les opérations à 0 % sont grisées, en bas.

type Sort = 'date' | 'amount'

interface Props {
    budget: Budget
    // Mois de la période, « 2026-07 », du plus ancien au plus récent
    months: string[]
    title: string
    // null pendant le chargement
    bankTransactions: BankTransaction[] | null
    accounts: Account[]
}

const impactCents = ({ amount_cents, impact }: BankTransaction) => Math.round(applyImpact(amount_cents, impact))
const isZero = ({ impact }: BankTransaction) => impactParts(impact)[0] === 0
const sum = (bankTransactions: BankTransaction[]) => bankTransactions.reduce((total, bankTransaction) => total + impactCents(bankTransaction), 0)
// Les opérations à 0 % en bas, l'ordre est gardé pour les autres
const zeroLast = (bankTransactions: BankTransaction[]) => [
    ...bankTransactions.filter((bankTransaction) => !isZero(bankTransaction)),
    ...bankTransactions.filter(isZero),
]

const BudgetOperations = ({ budget, months, title, bankTransactions, accounts }: Props) => {
    const [sort, setSort] = useState('date' as Sort)
    const account = (id: number) => accounts.find((account) => account.id === id)

    const row = (bankTransaction: BankTransaction) => {
        const origin = account(bankTransaction.account_id)
        const impact = impactCents(bankTransaction)
        const linked = bankTransaction.statement_links
            .map((link) => account(link.account_id))
            .filter((linkedAccount) => linkedAccount && linkedAccount.id !== bankTransaction.account_id) as Account[]
        return <div key={bankTransaction.id} className={`operation${isZero(bankTransaction) ? ' zero' : ''}`}>
            <span className="date">{ moment.utc(bankTransaction.at).format('D MMM') }</span>
            <span className="account" style={{ background: origin?.color }} title={origin?.name}>
                <i className={`fa-solid fa-${origin?.icon || 'wallet'}`}/>
            </span>
            <span className="label">
                <span className="main">
                    { bankTransaction.user_label || bankTransaction.label }
                    { linked.map((linkedAccount) => <span key={linkedAccount.id} className="badge link"><i className="fa-solid fa-link"/>{ linkedAccount.name }</span>) }
                    { bankTransaction.paid_by && <span className="badge paid">payé par { bankTransaction.paid_by }</span> }
                </span>
                { bankTransaction.user_label && <span className="bank">{ bankTransaction.label }</span> }
            </span>
            <span className={`amount${impact < 0 ? ' credit' : ''}`}>
                { formatCurrency(impact) }
                { bankTransaction.impact !== FULL_IMPACT && <small>{ formatImpact(bankTransaction.impact) } de { formatCurrency(bankTransaction.amount_cents) }</small> }
            </span>
        </div>
    }

    const byMonth = sort === 'date' && months.length > 1
    const content = () => {
        if(bankTransactions === null) return <div className="empty">Chargement…</div>
        if(!bankTransactions.length) return <div className="empty">Aucune opération { months.length > 1 ? 'sur cette période' : 'ce mois-ci' }</div>
        if(sort === 'amount') return zeroLast([...bankTransactions].sort((a, b) => impactCents(b) - impactCents(a))).map(row)
        if(!byMonth) return zeroLast(bankTransactions).map(row)
        return [...months].reverse().map((month) => {
            const monthTransactions = bankTransactions.filter(({ at }) => moment.utc(at).format('YYYY-MM') === month)
            return <div key={month}>
                <div className="month-head">
                    <span>{ moment(month, 'YYYY-MM').format('MMMM YYYY') }</span>
                    <b>{ formatCurrency(sum(monthTransactions)) }</b>
                </div>
                { monthTransactions.length ? zeroLast(monthTransactions).map(row) : <div className="empty small">Aucune opération</div> }
            </div>
        })
    }

    return <div className="tl-card budget-operations">
        <h3>
            <span className="title">
                Opérations · { budget.name }
                { bankTransactions && <small>{ bankTransactions.length } ligne{ bankTransactions.length > 1 ? 's' : '' }</small> }
            </span>
            <span className="sort">
                <button className={sort === 'date' ? 'on' : ''} onClick={() => setSort('date')}>Date</button>
                <button className={sort === 'amount' ? 'on' : ''} onClick={() => setSort('amount')}>Montant</button>
            </span>
        </h3>
        { content() }
        { bankTransactions && bankTransactions.length > 0 && <div className="total">
            <span>Total { title }</span>
            <span>{ formatCurrency(sum(bankTransactions)) }</span>
        </div> }
    </div>
}

export default BudgetOperations
